import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ShieldCheck, ShieldX, Loader2, AlertTriangle, ExternalLink, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function StoryReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/stories/review")
      .then(({ data }) => setItems(data || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const act = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.post(`/admin/stories/${id}/${action}`);
      toast.success(action === "approve" ? "Published" : "Rejected & removed");
      setItems((list) => list.filter((s) => s.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 pt-16 pb-20" data-testid="story-review-page">
      <div className="flex items-center gap-3 mb-2">
        <AlertTriangle className="text-[#F1A817]" size={26} />
        <h1 className="font-heading text-3xl sm:text-4xl uppercase tracking-tighter text-white">Story review queue</h1>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-8">Borderline submissions the AI couldn't clearly clear. Approve to publish, or reject to remove. (Content depicting minors is auto-blocked and never reaches this queue.)</p>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#00E5FF]" size={30} /></div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#1C1C1F] rounded-2xl py-20 text-center" data-testid="story-review-empty">
          <ShieldCheck className="text-[#31A24C] mx-auto mb-3" size={44} />
          <p className="text-[#52525B] font-mono text-xs uppercase tracking-widest">Queue is clear</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4" data-testid="story-review-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`story-review-item-${s.id}`} className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-white font-heading uppercase tracking-tight text-lg">{s.title}</h3>
                  <p className="text-[#8A8D91] font-mono text-xs mt-1">@{s.author_username} · {s.category} · {s.word_count} words</p>
                </div>
                <Link to={`/stories/${s.id}`} className="flex items-center gap-1 text-[#00E5FF] text-xs font-semibold hover:underline shrink-0"><ExternalLink size={13} /> Read</Link>
              </div>
              {s.safety_reason && (
                <div className="flex items-start gap-2 bg-[#F1A817]/10 border border-[#F1A817]/25 rounded-lg px-3 py-2 mt-3 text-[#F1A817] text-xs">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {s.safety_reason}
                </div>
              )}
              <p className="text-[#B0B3B8] text-sm mt-3 line-clamp-3">{s.text_content}</p>
              <div className="flex gap-3 mt-4">
                <button data-testid={`story-approve-${s.id}`} onClick={() => act(s.id, "approve")} disabled={busy[s.id]} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#31A24C]/15 text-[#31A24C] border border-[#31A24C]/40 hover:bg-[#31A24C]/25 text-sm font-semibold transition-colors disabled:opacity-50">
                  <ShieldCheck size={15} /> Approve
                </button>
                <button data-testid={`story-reject-${s.id}`} onClick={() => act(s.id, "reject")} disabled={busy[s.id]} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E60023]/10 text-[#E60023] border border-[#E60023]/40 hover:bg-[#E60023]/20 text-sm font-semibold transition-colors disabled:opacity-50">
                  <ShieldX size={15} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
